/* eslint-disable @typescript-eslint/no-explicit-any */
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid'; // Grid version 1
import Button from '@merq/web-ui/dist/components/Button';
import DatePicker from '@merq/web-ui/dist/components/DatePicker';
import Input from '@merq/web-ui/dist/components/Input';
import Loading from '@merq/web-ui/dist/components/Loading';
import useModal from 'components/Modal';
import { SelectorOption } from 'hooks/chartSelector';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useStore from 'store';
import { DEFAULT_METRIC, UUID } from 'store/Index/store';
import { getMetricLabel } from './utils';

const CHECKBOX_NAME = 'metric_checkbox';

interface Props {
  defaultMetric: string;
}
const MetricsDownloader: React.FC<Props> = ({ defaultMetric }) => {
  const [startDate, setStartDate] = React.useState<Dayjs | null>(dayjs().startOf('year'));
  const [endDate, setEndDate] = React.useState<Dayjs | null>(dayjs());
  const [loading, setLoading] = React.useState(false);
  const {
    handleSubmit,
    control,
    formState: { isValid },
    register,
  } = useForm({ defaultValues: { [defaultMetric]: true, roundingDecimals: 4 } });

  const [metrics, indexStore, priceHistory, getMetrics, getPriceHistory] = useStore(state => [
    state.metrics,
    state.index,
    state.priceHistory,
    state.getMetricsList,
    state.getPriceHistory,
  ]);

  const selectedIndex = indexStore.selected;

  const id =
    Object.keys(priceHistory.manifest).find(k => priceHistory.manifest[k] === selectedIndex?.id) ||
    '';

  useEffect(() => {
    if (selectedIndex) {
      getMetrics([{ type: 'index', id }]);
    }
  }, [selectedIndex]);

  const isDecrement = selectedIndex && selectedIndex?.spec?.indexClassCode === 'overlay-index-ext';

  const currentMetrics = (metrics.index[id] || []).filter(
    metric =>
      metric.description &&
      !metric.name.startsWith('secapi_') &&
      !metric.name.startsWith('sec_') &&
      !metric.name.startsWith('index_manifest') &&
      // do not display more metrics if it is a decrement index
      (metric.name === selectedIndex?.plotMetric || !isDecrement)
  );

  const setMetricLabel = (metricName: string) =>
    getMetricLabel(metricName, selectedIndex?.id || ('' as UUID), indexStore);

  const onSubmit = async (data: any) => {
    setLoading(true);
    const metricNames = currentMetrics.map(_ => _.name);
    const metricsQuery = Object.keys(data)
      .filter(
        _ =>
          _ !== 'roundingDecimals' &&
          !_.startsWith(CHECKBOX_NAME) &&
          !!data[_] &&
          metricNames.includes(_)
      )
      .map<SelectorOption>(metric => ({
        metric,
        label: selectedIndex?.name || '',
        value: selectedIndex?.id || ('' as any),
        type: 'index',
        isRealTime: false,
      }));

    const res = (await getPriceHistory(metricsQuery)) as any;
    const table = convertMetricsToTable(
      res.priceHistory.results[selectedIndex?.id || ''],
      startDate,
      endDate,
      Math.abs(data.roundingDecimals),
      setMetricLabel
    );
    downloadCsvFromTable(table, `${selectedIndex?.name}_metrics.csv`);

    setLoading(false);
  };

  return (
    <Loading height={100} active={loading}>
      <form onSubmit={handleSubmit(onSubmit)} className="consultation-form">
        <div className="checkbox-grid-container">
          <Grid
            container
            spacing={1}
            style={currentMetrics.length === 1 ? { display: 'none' } : {}}
          >
            {!!currentMetrics.length && (
              <Grid size={12}>
                <h2>Select metrics to download:</h2>
              </Grid>
            )}
            {currentMetrics.map(_ => (
              <Grid key={_.name} size={4}>
                <FormControlLabel
                  control={
                    <Controller
                      name={_.name}
                      control={control}
                      render={({ field: props }: any) => (
                        <Checkbox
                          {...props}
                          checked={!!props.value}
                          {...register(CHECKBOX_NAME, {
                            required: false,
                            validate: validateCheckbox,
                          })}
                          onChange={e => {
                            props.onChange(e.target.checked);
                            // validateCheckbox();
                          }}
                        />
                      )}
                    />
                  }
                  label={setMetricLabel(_.name)}
                />
              </Grid>
            ))}
          </Grid>
        </div>

        <Grid container spacing={2} style={{ marginTop: '15px' }}>
          <Grid size={3}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              label="From"
              maxDate={endDate || undefined}
              variant="outlined"
            />
          </Grid>
          <Grid size={3}>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              label="To"
              maxDate={dayjs()}
              minDate={startDate || undefined}
              variant="outlined"
            />
          </Grid>
          <Grid size={3}>
            <Controller
              control={control}
              name="roundingDecimals"
              render={({ field }) => {
                return (
                  <Input
                    {...field}
                    type="number"
                    label="Rounding Decimals"
                    variant="outlined"
                    size="small"
                    slotProps={{
                      input: { style: { padding: '8px', width: '140px' } },
                      htmlInput: { min: 0, max: 13 },
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid size={3}>
            <Button
              color="primary"
              variant="contained"
              style={{ marginLeft: 'auto' }}
              type="submit"
              disabled={!isValid}
              fullWidth
            >
              Download
            </Button>
          </Grid>
        </Grid>
      </form>
    </Loading>
  );
};

const useMetricsDownloader = (defaultMetric = DEFAULT_METRIC) =>
  useModal(<MetricsDownloader defaultMetric={defaultMetric || DEFAULT_METRIC} />, {
    title: 'Download CSV',
  });

export default useMetricsDownloader;

function validateCheckbox() {
  const mediumCheckboxes = document.getElementsByName('metric_checkbox');
  let okay = false;

  for (let i = 0, len = mediumCheckboxes.length; i < len; i++) {
    if ((mediumCheckboxes[i] as HTMLInputElement).checked) {
      okay = true;
      break;
    }
  }

  return okay;
}

type InputObject = { [metric: string]: Array<[date: number, value: number]> };
type OutputObject = { head: Array<string>; body: Array<Array<string | number>> };

function convertMetricsToTable(
  metrics: InputObject,
  startDate: Dayjs | null,
  endDate: Dayjs | null,
  roundingDecimals: number,
  setMetricLabel: (metricName: string) => string
): OutputObject {
  const columnValues: Map<number, Map<string, number>> = new Map();

  // Loop through metrics properties and add their values to columnValues
  for (const metric in metrics) {
    const metricData = metrics[metric];
    metricData.forEach(([dat, val]) => {
      if ((!startDate || dat >= startDate.valueOf()) && (!endDate || dat <= endDate.valueOf())) {
        if (!columnValues.has(dat)) {
          columnValues.set(dat, new Map());
        }
        columnValues.get(dat)!.set(metric, parseFloat(val.toFixed(roundingDecimals)));
      }
    });
  }

  // Convert columnValues to the desired output format
  const head = ['Date', ...Object.keys(metrics).map(setMetricLabel)];
  const body = Array.from(columnValues.entries()).map(([dat, valuesMap]) => {
    const values = Array.from(valuesMap.values());
    return [dayjs.utc(dat).format('YYYY-MM-DD'), ...values];
  });

  return { head, body };
}

function downloadCsvFromTable(table: OutputObject, filename: string) {
  const csvRows = [table.head, ...table.body].map(row => row.join(','));
  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
